import { useVirtualizer } from '@tanstack/react-virtual';
import { forwardRef, memo, useImperativeHandle, useRef } from 'react';

import { useGetTopVisibleElementIndex } from '../hooks/useGetTopVisibleElementIndex';
import { isiOS } from '../utils/isiOS';

export type PDFPagesVirtualizedListProps = {
  rowsCount: number;
  children: (props: { index: number }) => JSX.Element | null;
  getPageHeight: (index: number) => number;
  onTopVisibleElementChange: (index: number) => void;
};

export type ScrollAlign = 'start' | 'center' | 'end' | 'auto';

export type PDFVirtualizedListHandle = {
  getScrollElement: () => HTMLDivElement | null;
  scrollToIndex: (index: number, align?: ScrollAlign) => void;
  updateItemsSize: () => void;
};

export const DEFAULT_PDF_PAGE_HEIGHT = 500;
export const PDF_LIST_SCROLL_MARGIN = 12;
export const PDF_PAGE_VERTICAL_PADDING = 8;

const _PDFPagesVirtualizedList = forwardRef<PDFVirtualizedListHandle, PDFPagesVirtualizedListProps>(
  ({ rowsCount, children, getPageHeight, onTopVisibleElementChange }: PDFPagesVirtualizedListProps, forwardedRef) => {
    const listRef = useRef<HTMLDivElement | null>(null);
    const scrollElementRef = useRef<HTMLDivElement | null>(null);

    const virtualizer = useVirtualizer({
      count: rowsCount,
      estimateSize: (index) => getPageHeight(index) + 2 * PDF_PAGE_VERTICAL_PADDING,
      getScrollElement: () => scrollElementRef.current,
      overscan: isiOS() ? 1 : 6, // ios is very limited in terms of canvas memory
      paddingEnd: 30,
      scrollMargin: PDF_LIST_SCROLL_MARGIN,
    });
    virtualizer.shouldAdjustScrollPositionOnItemSizeChange = () => false;

    useGetTopVisibleElementIndex({ onTopVisibleElementChange, virtualizer });

    useImperativeHandle(
      forwardedRef,
      () => ({
        getScrollElement: () => virtualizer.scrollElement,
        scrollToIndex: (index, align = 'start') => virtualizer.scrollToIndex(index, { align }),
        updateItemsSize: () => virtualizer.measure(),
      }),
      [virtualizer],
    );

    const virtualItems = virtualizer.getVirtualItems();

    return (
      <div
        className="scrollbar scrollbar-track-gray-2 scrollbar-thumb-gray-7 [&:not(:hover)]:scrollbar-track-transparent w-full flex-1 overflow-auto pl-4"
        data-testid="PDF_PAGES_LIST"
        ref={scrollElementRef}
      >
        <div
          className="relative w-full"
          ref={listRef}
          style={{
            height: virtualizer.getTotalSize(),
          }}
        >
          <div
            className="absolute top-0 left-0 w-full"
            style={{
              transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
            }}
          >
            {virtualItems.map((virtualRow) => (
              <div className="py-2" data-index={virtualRow.index} key={virtualRow.key} ref={virtualizer.measureElement}>
                {children({ index: virtualRow.index })}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  },
);

_PDFPagesVirtualizedList.displayName = 'PDFPagesVirtualizedList';

export const PDFPagesVirtualizedList = memo(_PDFPagesVirtualizedList) as typeof _PDFPagesVirtualizedList;

PDFPagesVirtualizedList.displayName = 'PDFPagesVirtualizedList';
